"use client";

import { AnimatePresence, LayoutGroup, motion } from "motion/react";

import { useWhatsAppOrderSubmit } from "@/components/cart/use-whatsapp-order-submit";
import { WhatsAppIcon } from "@/components/ui/whatsapp-icon";
import { usePressedInteraction } from "@/components/ui/motion-primitives";
import {
  FloatingWhatsAppButton,
  whatsappMorphLayoutId,
} from "@/components/whatsapp/floating-whatsapp-button";
import { useMorphTargetVisibility } from "@/components/whatsapp/use-morph-target-visibility";

export interface CartWhatsAppOrderActionsProps {
  readonly customerPhone: string;
  readonly onPhoneInvalid?: () => void;
}

export function CartWhatsAppOrderActions({
  customerPhone,
  onPhoneInvalid,
}: Readonly<CartWhatsAppOrderActionsProps>) {
  const {
    canRenderAction,
    errorMessage,
    isDisabled,
    status,
    submitOrder,
    warningMessage,
    warningWhatsappUrl,
  } = useWhatsAppOrderSubmit(customerPhone, onPhoneInvalid);
  const { shouldRenderFloatingTarget, targetRef } =
    useMorphTargetVisibility<HTMLButtonElement>();
  const interaction = usePressedInteraction();

  return (
    <LayoutGroup id="cart-whatsapp-order">
      <div>
        <motion.button
          aria-busy={status === "pending"}
          className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-secondary px-4 text-sm font-extrabold text-white disabled:bg-outline disabled:text-white"
          disabled={isDisabled}
          layoutId={whatsappMorphLayoutId}
          onClick={submitOrder}
          ref={targetRef}
          type="button"
          {...interaction}
        >
          <WhatsAppIcon className="h-5 w-5 brightness-0 invert" variant="mono" />
          {status === "pending" ? "Enviando..." : "Enviar pedido por WhatsApp"}
        </motion.button>
        {errorMessage ? (
          <p className="mt-2 rounded-lg border border-error bg-red-50 p-2 text-sm font-bold text-error">
            {errorMessage}
          </p>
        ) : null}
        {warningMessage ? (
          <p className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-sm font-bold text-amber-900">
            {warningMessage}
          </p>
        ) : null}
        {warningWhatsappUrl ? (
          <div className="mt-2">
            <p className="text-sm leading-6 text-muted">
              La cotización interna no se pudo registrar; la tienda la confirmará por WhatsApp.
            </p>
            {/* An anchor navigation is used because `window.open` with
                `noopener` cannot indicate whether a popup was blocked, while a
                same-tab fallback would replace the cart page and hide the
                warning. */}
            <a
              className="mt-2 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-lg bg-secondary px-4 text-sm font-extrabold text-white"
              href={warningWhatsappUrl}
              rel="noopener noreferrer"
              target="_blank"
            >
              <WhatsAppIcon className="h-5 w-5 brightness-0 invert" variant="mono" />
              Continuar a WhatsApp
            </a>
          </div>
        ) : null}
      </div>
      <AnimatePresence>
        {shouldRenderFloatingTarget && canRenderAction ? (
          <FloatingWhatsAppButton
            disabled={isDisabled}
            isBusy={status === "pending"}
            key="cart-whatsapp-fab"
            onClick={submitOrder}
            type="button"
          />
        ) : null}
      </AnimatePresence>
    </LayoutGroup>
  );
}
